/**
 * Crash reporting.
 *
 * Firebase Crashlytics has no web SDK — every method on
 * @capacitor-firebase/crashlytics throws "not implemented on web" outside a
 * native shell, so this module guards every native call with
 * Capacitor.isNativePlatform() (same pattern as lib/ads.ts). Native crashes
 * (JVM/NDK) are captured automatically by the plugin once installed; what we
 * wire by hand here is:
 *
 *   1. Unhandled JS errors / promise rejections, native-side, via
 *      recordException() — these would otherwise just show up as a console
 *      error with no Crashlytics report.
 *   2. The same JS errors on the web/iOS PWA build, best-effort, as a GA4
 *      `exception` event — Crashlytics can't run there, but this keeps
 *      failures from being completely invisible on that platform.
 *   3. User attribution, so a crash can be tied back to an account.
 */

import { Capacitor } from "@capacitor/core";
import { FirebaseCrashlytics } from "@capacitor-firebase/crashlytics";

import { logAnalyticsEvent } from "./analytics";

const isDev = import.meta.env.DEV;
const isNative = Capacitor.isNativePlatform();

function describe(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  try {
    return String(error);
  } catch {
    return "Unknown error";
  }
}

/** Record a caught (non-fatal) error. Safe to call from any platform. */
export function recordException(error: unknown, context?: string): void {
  const message = context ? `[${context}] ${describe(error)}` : describe(error);
  if (isDev) console.error("[crashlytics]", message, error);

  if (isNative) {
    FirebaseCrashlytics.recordException({ message }).catch((err) => {
      if (isDev) console.warn("[crashlytics] recordException failed", err);
    });
  } else {
    // No Crashlytics on web — fall back to an Analytics event so the failure
    // isn't silently dropped. `fatal: false` mirrors GA4's own convention
    // for this event name.
    logAnalyticsEvent("exception", { description: message.slice(0, 100), fatal: false });
  }
}

/** Mirrors auth state so native crash reports can be tied to an account. */
export function setCrashlyticsUserId(userId: string | null): void {
  if (!isNative || !userId) return;
  FirebaseCrashlytics.setUserId({ userId }).catch((err) => {
    if (isDev) console.warn("[crashlytics] setUserId failed", err);
  });
}

let installed = false;

/** Call once at boot. Catches whatever React's own error boundary (if any) misses. */
export function installGlobalErrorHandlers(): void {
  if (installed) return;
  installed = true;

  window.addEventListener("error", (event) => {
    recordException(event.error ?? event.message, "window.onerror");
  });

  window.addEventListener("unhandledrejection", (event) => {
    recordException(event.reason, "unhandledrejection");
  });
}
